import { Outlet, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { SECTION_PERMISSIONS, type SectionKey } from '@/types/permissions';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import AccessDenied from './AccessDenied';

interface RequireSectionProps {
  section: SectionKey;
  /**
   * Name of a route param that, when it equals the signed-in admin's own id,
   * lets the route through regardless of `section`. Used for an admin's own
   * profile pages: `update-admin-user` allows any admin to edit their own
   * name and email, so the URL must stay reachable for staff and managers.
   */
  allowSelfParam?: string;
}

/**
 * Route-level permission guard. Wraps a group of routes so the URL itself is
 * gated, not just the sidebar link — a staff admin typing /admin-users gets
 * the same denial the nav already implies.
 *
 * This is the client-side boundary only. Anything that must hold against a
 * direct PostgREST or edge-function call is enforced server-side (RLS, edge
 * function tier checks); this guard keeps the UI honest.
 */
export default function RequireSection({ section, allowSelfParam }: RequireSectionProps) {
  const { hasAccessTo, isLoading } = usePermissions();
  const { session } = useAuth();
  const params = useParams();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const selfId = allowSelfParam ? params[allowSelfParam] : undefined;
  const isSelf = Boolean(selfId) && selfId === session?.user?.id;

  if (!hasAccessTo(section) && !isSelf) {
    return <AccessDenied sectionName={SECTION_PERMISSIONS[section].name} />;
  }

  return <Outlet />;
}
